import {
  Link,
  LoaderFunctionArgs,
  useLoaderData,
  useNavigate,
  useRevalidator,
} from "react-router-dom";
import { Badge } from "../../components/Badge";
import { createApolloClient } from "../../providers/ApolloClientFactory";
import {INVOICES_LIST_QUERY} from "./queries";
import { organizationStore } from "../../store/organization";
import { useEffect, useRef, useState } from "react";
import { eventStore } from "../../store/event";
import moment from "moment/moment";
import { usePermissions } from "../auth/api";
import {
  ColDef,
  GridApi,
  SizeColumnsToContentStrategy,
} from "ag-grid-community";
import { Breadcrumbs } from "../../components/Breadcrumbs";
import { Input } from "../../components/inputs/Input";
import { PrimaryButton, SecondaryButton } from "../../components/Button";
import { AgGridReact } from "ag-grid-react";
import {Invoice} from "./type";
import {usdFormat} from "../../utils";

const client = createApolloClient();
const { getState } = organizationStore;
const { getState: getEventStore } = eventStore;
const buildSearch = (search: string) => [
  {
    role: {
      name: {
        _ilike: `%${search}%`,
      },
    },
  },
  {
    phone_number: {
      _ilike: `%${search}%`,
    },
  },
  {
    username: {
      _ilike: `%${search}%`,
    },
  },
];

type CellRenderer = (params: {
  value:
    | string
    | number
    | boolean
    | React.ReactElement<any, string | React.JSXElementConstructor<any>>
    | React.ReactFragment
    | React.ReactPortal
    | null
    | undefined;
  data: Invoice;
}) => JSX.Element | null;
const createCellRenderer = (
  permission: boolean,
  linkFunc: (params: { data: Invoice }) => string
): CellRenderer => {
  return (params) => {
    if (params.value === undefined) {
      return null;
    }

    return (
      <>
        {permission ? (
          <Link to={linkFunc(params)}>
            <Badge>{params.value}</Badge>
          </Link>
        ) : (
          <Badge>{params.value}</Badge>
        )}
      </>
    );
  };
};

export const invoicesListLoader = async ({ request }: LoaderFunctionArgs) => {
  const url = new URL(request.url);
  const search = url.searchParams.get("search");
  const organizationId = getState().organizationId;
  const eventId = getEventStore().eventId;

  let where: any = { _or: search ? buildSearch(search) : undefined };
  if (organizationId) {
    where = {
      ...where,
      organization_id: {
        _eq: organizationId,
      },
    };
  }
  if (eventId) {
    where = {
      ...where,
      event_id: {
        _eq: eventId,
      },
    };
  }
  const { data } = await client.query({
    query: INVOICES_LIST_QUERY,
    variables: {
      where,
    },
  });
  return data.invoices;
};

export const InvoicesList = () => {
  const { editCreatePermission, ListPermission, moduleWisePermission } =
    usePermissions("invoices");
  const { editCreatePermission: eventsProcessorEditPermission } =
    usePermissions("events");
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const gridRef = useRef<GridApi>();
  const invoices = useLoaderData() as Invoice[];
  const [rowData, setRowData] = useState<Invoice[]>([]);
  useEffect(() => {
    setRowData(invoices);
  }, [invoices]);
  const generateColDefs = (): ColDef<Invoice>[] => [
    {
      headerName: "Id",
      valueGetter: (p) => p.data?.id,
      cellRenderer: createCellRenderer(
        editCreatePermission,
        (p) => `/invoices/${p.data.id}/edit`
      ),
      filter: "agTextColumnFilter",
      pinned: "left",
    },
    {
      headerName: "Invoice Number",
      valueGetter: (p) => p.data?.invoice_number,
      cellRenderer: createCellRenderer(
        editCreatePermission,
        (p) => `/invoices/${p.data.id}/edit`
      ),
      filter: "agTextColumnFilter",
      pinned: "left",
    },
    {
      headerName: "Status",
      valueGetter: (p) => p.data?.status,
    },
    {
      headerName: "To Name",
      valueGetter: (p) => p.data?.bill_to_name,
      filter: "agTextColumnFilter",
    },
    {
      headerName: "To Email",
      valueGetter: (p) => p.data?.bill_to_email,
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Invoice Grand Total",
      valueGetter: (p) => p.data?.grand_total_amount,
      valueFormatter: (params) => usdFormat.format((params.value ?? 0)),
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Invoice Date",
      valueGetter: (p) => p.data?.invoice_date,
      valueFormatter: (p) => {
        const date = p.value ? moment(p.value) : null;
        return date ? date.format("MM/DD/YYY") : "";
      },
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Invoice Due Date",
      valueGetter: (p) => p.data?.invoice_due_date,
      valueFormatter: (p) => {
        const date = p.value ? moment(p.value) : null;
        return date ? date.format("MM/DD/YYYY") : "";
      },
      filter: "agTextColumnFilter",
    },
    {
      headerName: "From Name",
      valueGetter: (p) => p.data?.from_name,
      filter: "agTextColumnFilter",
    },
    {
      headerName: "From Email",
      valueGetter: (p) => p.data?.from_email,
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Event",
      valueGetter: (p) => p.data?.invoice_event?.name,
      cellRenderer: createCellRenderer(
        eventsProcessorEditPermission,
        (p) => `/events/${p.data.event_id}/edit`
      ),
      filter: "agTextColumnFilter",
    },
    {
      headerName: "Updated By",
      valueGetter: (p) => p.data?.invoice_user?.username,
    },
    {
      headerName: "Created Date",
      valueGetter: (p) => p.data?.created_at,
      valueFormatter: (p) => {
        const date = p.value ? moment(p.value) : null;
        return date ? date.format("MM/DD/YYYY h:mm:ss a") : "";
      },
    },
    {
      headerName: "Updated Date",
      valueGetter: (p) => p.data?.updated_at,
      valueFormatter: (p) => {
        const date = p.value ? moment(p.value) : null;
        return date ? date.format("MM/DD/YYYY h:mm:ss a") : "";
      },
    },
  ];
  const colDefs: ColDef<Invoice>[] = generateColDefs();
  const rowHeight = 50;
  const autoSizeStrategy: SizeColumnsToContentStrategy = {
    type: "fitCellContents",
  };
  const handleExportCSV = () => {
    if (gridRef.current) {
      const timestamp = new Date().toISOString().replace(/[-:]/g, "");
      const params = {
        fileName: `Invoices_${timestamp}.csv`,
        columnSeparator: ",",
      };
      gridRef.current.exportDataAsCsv(params);
    }
  };
  const gridOptions = {
    enableCellTextSelection: true,
  };
  return (
    <div style={{ height: "75vh" }}>
      <div
        className={"ag-theme-quartz"}
        style={{ width: "100%", height: "100%" }}
      >
        <div className=" grid gap-2 grid-cols-6">
          <Breadcrumbs />
          <div className="col-span-2 col-start-5 mt-2">
            <Input
              name="search"
              placeholder={`Search Invoices`}
              onChange={(s) => {
                navigate(
                  s && typeof s === "string" && s.length > 0
                    ? `/invoices/list?search=${s}`
                    : `/invoices/list`
                );
                revalidator.revalidate();
              }}
            />
          </div>
          <div className="py-2 col-span-2 col-start-7">
            <SecondaryButton onClick={handleExportCSV}>
              Export to CSV
            </SecondaryButton>
            {editCreatePermission && (
              <PrimaryButton onClick={() => navigate(`/invoices/create`)}>
                Create
              </PrimaryButton>
            )}
          </div>
        </div>
        <AgGridReact
          rowData={rowData}
          columnDefs={colDefs}
          rowHeight={rowHeight}
          autoSizeStrategy={autoSizeStrategy}
          pagination={true}
          gridOptions={gridOptions}
          paginationPageSize={100}
          onGridReady={(params) => (gridRef.current = params.api)}
        />
      </div>
    </div>
  );
};
